import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import { Select as SelectJoy, selectClasses, Option, optionClasses, styled, useTheme } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types/theme';
import { useTranslation } from 'next-i18next';
import { ChangeEvent } from 'react';

export type SelectOption = {
  label: string;
  value: string;
};

export interface SelectProps {
  /** Reaches the button, so a `<label htmlFor>` points at it. */
  id?: string;
  name?: string;
  value?: string | string[];
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: Array<SelectOption>;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  multiple?: boolean;
  onListboxScroll?: (event: React.UIEvent<HTMLUListElement>) => void;
  loading?: boolean;
  sx?: SxProps;
  clearable?: boolean;
}

const Select = ({
  clearable = true,
  onChange,
  options,
  multiple,
  onListboxScroll,
  loading,
  value,
  error,
  ...rest
}: SelectProps) => {
  const theme = useTheme();
  const { t } = useTranslation('common');

  const optionElements = options.map(option => (
    <OptionStyled key={option.label} value={option.value}>
      {option.label}
    </OptionStyled>
  ));

  if (loading) {
    optionElements.push(
      <OptionStyled key="loading" value="" disabled>
        {t('user.loading')}
      </OptionStyled>,
    );
  }

  const onChangeHandler = (_: unknown, newValue: string) => {
    if (!onChange) {
      return;
    }
    onChange({
      target: {
        name: rest.name,
        value: newValue,
      },
    } as ChangeEvent<HTMLSelectElement>);
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange({
        target: {
          name: rest.name,
          value: multiple ? [] : '',
        },
      } as ChangeEvent<HTMLSelectElement>);
    }
  };

  const showReset =
    clearable &&
    ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value !== '' && value != null));

  const isEmpty = multiple ? !Array.isArray(value) || value.length === 0 : value === '' || value == null;

  const placeholderSx: SxProps = {};

  return (
    <SelectStyled
      multiple={multiple}
      onChange={onChangeHandler as never}
      value={value}
      error={error ? 'true' : undefined}
      color={error ? 'danger' : 'neutral'}
      endDecorator={
        showReset ? (
          <ResetButton type="button" aria-label={t('label.clearSelection')} onClick={handleReset}>
            <Icon name={IconName.close} size={16} color={theme.palette.grey4} />
          </ResetButton>
        ) : undefined
      }
      indicator={<Icon name={IconName.chevronDown} size={22} color={theme.palette.grey4} />}
      slotProps={{
        button: {
          sx: {
            ...placeholderSx,
            ...(!isEmpty && { color: theme.vars.palette.black }),
            '&.MuiSelect-button': {
              ...(!isEmpty && { color: theme.vars.palette.black }),
              ...(isEmpty && { color: theme.vars.palette.grey4 }),
            },
          },
        },
        listbox: {
          sx: {
            p: 0,
            m: 0,
            borderRadius: '2px',
            border: `1px solid ${theme.vars.palette.grey1}`,
            background: theme.vars.palette.white,
            gap: 0,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
            maxHeight: '300px',
            overflowY: 'auto',
            '& > li': {
              p: 0,
              m: 0,
            },
          },
          onScroll: onListboxScroll,
        },
        indicator: {
          sx: {
            '--Icon-fontSize': '24px',
            color: theme.vars.palette.grey4,
          },
        },
        root: {
          sx: {
            '& .MuiSelect-endDecorator': {
              zIndex: 1,
              position: 'relative',
            },
          },
        },
      }}
      {...rest}
    >
      {optionElements}
    </SelectStyled>
  );
};

export default Select;

const whiteBackgroundSx: SxProps = {
  background: theme => theme.vars.palette.white,
  '&:hover': {
    background: theme => theme.vars.palette.white,
  },
};

export const WhiteSelect = (props: SelectProps) => {
  const mergedSx: SxProps = Array.isArray(props?.sx) ? [...props.sx, whiteBackgroundSx] : [props.sx, whiteBackgroundSx];
  return <Select {...props} sx={mergedSx} />;
};

const SelectStyled = styled(SelectJoy)<{ error?: string }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  gap: 8px;
  height: 36px;

  background: ${({ theme }) => theme.vars.palette.grey1};
  border-radius: 2px;
  box-shadow: none;
  border: ${({ theme, error }) => (error ? `1px solid ${theme.vars.palette.orange1}` : '1px solid transparent')};

  ${({ theme }) => theme.typography['body-small']}
  color: ${({ theme }) => theme.vars.palette.black};

  .${selectClasses.button} {
    padding: 0;
    font: inherit;
    min-height: unset;
    min-width: unset;
    color: ${({ theme }) => theme.vars.palette.black};
  }

  &:hover:not(.Mui-disabled) {
    border-color: ${({ theme, error }) => (error ? theme.vars.palette.orange1 : theme.vars.palette.grey2)};
  }

  &:focus {
    outline: none;
    border-color: transparent;
  }

  &:hover {
    background: ${({ theme }) => theme.vars.palette.grey1};
  }

  &.${selectClasses.disabled} {
    background: ${({ theme }) => theme.vars.palette.grey2};
    color: ${({ theme }) => theme.vars.palette.grey4};
  }

  &.${selectClasses.disabled} .${selectClasses.button} {
    cursor: not-allowed !important;
    pointer-events: all !important;
  }

  .MuiSelect-endDecorator {
    margin-right: -8px;
  }
`;

export const ResetButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  position: relative;
  z-index: 1;
`;

const OptionStyled = styled(Option)`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px;
  gap: 8px;
  height: 40px;
  width: 100%;
  margin: 0;
  min-height: unset;

  ${({ theme }) => theme.typography['body-small']}
  color: ${({ theme }) => theme.vars.palette.black};

  border-bottom: 1px solid ${({ theme }) => theme.vars.palette.grey1};

  &.${optionClasses.selected} {
    background: ${({ theme }) => theme.vars.palette.grey2};
    margin: 0;
    padding: 12px;
  }

  &.${optionClasses.root} {
    margin: 0;
    padding: 12px;

    &:hover {
      background: ${({ theme }) => theme.vars.palette.grey2};
    }
  }
`;
